import React,{useEffect,useRef,useState} from "react";

const emptyQuestion=()=>({text:"",criteria:[{name:"",evidence_definition:""}]});
const progressLabels=[
  ["links_ready","Links ready"],["emailed","Emailed"],["opened","Opened"],
  ["started","Started"],["completed","Completed"],["failed","Failed"]
];

export default function AssessmentCampaign({request,role,policy,settings,formats}){
  const [sets,setSets]=useState([]),[eligible,setEligible]=useState([]),[selected,setSelected]=useState([]);
  const [questions,setQuestions]=useState([emptyQuestion()]),[questionSetId,setQuestionSetId]=useState("");
  const [sendEmail,setSendEmail]=useState(true),[busy,setBusy]=useState(false),[error,setError]=useState("");
  const [campaign,setCampaign]=useState(null),[links,setLinks]=useState({});
  const submission=useRef(null);
  const jobId=role?.id;
  useEffect(()=>{
    if(!jobId)return;
    let alive=true;
    setCampaign(null);setSelected([]);setQuestionSetId("");setLinks({});
    Promise.all([
      request("/api/assessments/question-sets?job_id="+encodeURIComponent(jobId)),
      request("/api/assessments/jobs/"+encodeURIComponent(jobId)+"/eligible-workflows"),
      request("/api/assessments/jobs/"+encodeURIComponent(jobId)+"/campaigns")
    ]).then(([questionSets,workflows,history])=>{
      if(!alive)return;
      setSets(questionSets);setQuestionSetId(questionSets[0]?.id||"");
      setEligible(workflows.workflows||[]);setCampaign(history.campaigns?.[0]||null);
    }).catch(e=>{if(alive)setError(e.message);});
    return()=>{alive=false;};
  },[jobId]);
  useEffect(()=>{
    if(!campaign?.id)return;
    const refresh=()=>request("/api/assessments/campaigns/"+campaign.id).then(setCampaign).catch(()=>{});
    const timer=setInterval(refresh,15000);
    return()=>clearInterval(timer);
  },[campaign?.id]);
  function editQuestion(index,patch){setQuestions(old=>old.map((q,i)=>i===index?{...q,...patch}:q));}
  async function publish(){
    setBusy(true);setError("");
    try{
      const published=await request("/api/assessments/question-sets",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({job_id:jobId,questions,formats,policy,settings})
      });
      setSets(old=>[published,...old]);setQuestionSetId(published.id);
    }catch(e){setError(e.message);}finally{setBusy(false);}
  }
  async function launch(){
    if(!selected.length||!questionSetId)return;
    const workflowIds=[...selected].sort();
    const signature=JSON.stringify([jobId,questionSetId,workflowIds,sendEmail]);
    if(submission.current?.signature!==signature)submission.current={signature,key:crypto.randomUUID()};
    setBusy(true);setError("");
    try{
      const result=await request("/api/assessments/campaigns",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({job_id:jobId,question_set_id:questionSetId,workflow_ids:workflowIds,
          send_email:sendEmail,idempotency_key:submission.current.key})
      });
      setCampaign(result);setLinks(result.new_links||{});setSelected([]);
      const data=await request("/api/assessments/jobs/"+encodeURIComponent(jobId)+"/eligible-workflows");
      setEligible(data.workflows||[]);
    }catch(e){setError(e.message);}finally{setBusy(false);}
  }
  if(!jobId)return null;
  const validQuestions=questions.every(q=>q.text.trim()&&q.criteria.every(c=>c.name.trim()&&c.evidence_definition.trim()));
  const validPolicy=Boolean(policy.organization?.trim()&&policy.contact_email?.includes("@")&&
    Number.isInteger(policy.retention_days)&&policy.retention_days>=1&&policy.retention_days<=365);
  const available=eligible.filter(w=>!w.has_active_link);
  return <details className="assessment-campaign">
    <summary>Assess several candidates</summary>
    <p>Prepare one question set, choose candidates at the assessment stage, and send their private links. Each candidate is reviewed separately.</p>
    {sets.length>0&&<label>Question set to send<select value={questionSetId} onChange={e=>setQuestionSetId(e.target.value)}>{sets.map(q=><option key={q.id} value={q.id}>Version {q.version} — {q.questions.length} question{q.questions.length===1?"":"s"}</option>)}</select><small className="assessment-campaign-hint">Each version is a saved set of questions. Pick one to send, or create a new set below.</small></label>}
    <details className="assessment-campaign-editor"><summary>Create shared questions</summary>
      {questions.map((q,index)=><div className="assessment-campaign-question" key={index}>
        <label>Question {index+1}<textarea value={q.text} maxLength={1000} onChange={e=>editQuestion(index,{text:e.target.value})} placeholder="Ask for a concrete work example or decision"/></label>
        {q.criteria.map((criterion,i)=><div className="assessment-campaign-criterion" key={i}>
          <label>Skill or criterion<input value={criterion.name} maxLength={120} onChange={e=>editQuestion(index,{criteria:q.criteria.map((c,n)=>n===i?{...c,name:e.target.value}:c)})}/></label>
          <label>Evidence to look for<input value={criterion.evidence_definition} maxLength={800} onChange={e=>editQuestion(index,{criteria:q.criteria.map((c,n)=>n===i?{...c,evidence_definition:e.target.value}:c)})}/></label>
          {q.criteria.length>1&&<button type="button" onClick={()=>editQuestion(index,{criteria:q.criteria.filter((_,n)=>n!==i)})}>Remove criterion</button>}
        </div>)}
        <div className="assessment-campaign-actions">
          {q.criteria.length<5&&<button type="button" onClick={()=>editQuestion(index,{criteria:[...q.criteria,{name:"",evidence_definition:""}]})}>Add criterion</button>}
          {questions.length>1&&<button type="button" onClick={()=>setQuestions(old=>old.filter((_,n)=>n!==index))}>Remove question</button>}
        </div>
      </div>)}
      <div className="assessment-campaign-actions">
        {questions.length<8&&<button type="button" onClick={()=>setQuestions(old=>[...old,emptyQuestion()])}>Add question</button>}
        <button type="button" className="primary" disabled={busy||!validQuestions||!validPolicy||!formats.length} onClick={publish}>Publish questions</button>
      </div>
      <small>Published questions and their rubric cannot be edited after links are issued. Candidates see each question, but only recruiters see the rubric.</small>
    </details>
    <fieldset><legend>Candidates ready for assessment</legend>
      {available.length?available.map(w=><label key={w.workflow_id} className="assessment-campaign-candidate"><input type="checkbox" checked={selected.includes(w.workflow_id)} disabled={busy||(!selected.includes(w.workflow_id)&&selected.length>=20)} onChange={e=>setSelected(old=>e.target.checked?[...old,w.workflow_id]:old.filter(id=>id!==w.workflow_id))}/>{w.candidate_name}</label>):<p>All candidates at this stage already have a link, or no candidate workflows are ready.</p>}
    </fieldset>
    <label className="assessment-campaign-candidate"><input type="checkbox" checked={sendEmail} onChange={e=>setSendEmail(e.target.checked)}/>Send through connected Gmail when an address is available</label>
    <button type="button" className="primary" disabled={busy||!selected.length||!questionSetId} onClick={launch}>{busy?"Working…":"Create "+selected.length+" private links"}</button>
    {error&&<p role="alert" className="assessment-error">{error}</p>}
    {campaign&&<section className="assessment-campaign-progress" aria-live="polite">
      <h4>Campaign progress <small>{campaign.counts.total} candidates</small></h4>
      <div className="assessment-evidence-counts">{progressLabels.map(([key,label])=><span key={key}><strong>{campaign.counts[key]}</strong> {label}</span>)}</div>
      <ul>{campaign.members.map(m=><li key={m.workflow_id}><b>{m.candidate_name}</b> · {m.submitted_at?"Completed":m.delivery_status.replaceAll("_"," ")}
        {m.delivery_message&&<small> · {m.delivery_message}</small>}
        {links[m.workflow_id]&&<button type="button" onClick={()=>navigator.clipboard?.writeText(links[m.workflow_id])}>Copy new link</button>}
      </li>)}</ul>
    </section>}
  </details>;
}
